import React, { useState } from "react";
import { Upload, Button, Modal, Table, message, Result } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import map from "lodash/map";
import get from "lodash/get";

import {
    parseExcelFile,
    validateExcelData,
    generateStaffColumns,
    generateStaffMonthlyPayload,
} from "../../utilities/staffDetailManagement";

import {
    REQUIRED_HEADERS_DAILY,
    REQUIRED_HEADERS_MONTHLY,
} from "../../constants/staffDetailManagement";
import { useDispatch } from "react-redux";
import { importAttendence, importStaff } from "../../redux/actions/companyActions";
import { STAFF_ATTENDENCE_IMPORT_FAILED, STAFF_ATTENDENCE_IMPORT_SUCCESS, STAFF_IMPORT_FAILED, STAFF_IMPORT_SUCCESS } from "../../constants/message";
import HeadersMissingModal from "./headersMissingModal";

const StaffAttendance = ({ fileType, removeSNFields = true, title, dateString, filter, config, staffs, updateData }) => {
    const [uploading, setUploading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const dispatch = useDispatch()
    const [headers, setHeaders] = useState([]);
    const [missingHeadersModal, setMissingHeadersModal] = useState(false);
    const [missingHeaders, setMissingHeaders] = useState([]);

    const [rows, setRows] = useState([]);
    const [rowErrors, setRowErrors] = useState({});
    const [validationData, setValidationData] = useState([]);

    const requiredHeaders =
        fileType === "monthly" ? REQUIRED_HEADERS_MONTHLY : REQUIRED_HEADERS_DAILY;

    const handleUpload = (file) => {
        if (!file) return message.warning("Please select a file.");

        setUploading(true);

        parseExcelFile(
            file,
            (sheetHeaders, sheetData) => {
                const missing = requiredHeaders.filter(
                    (h) => !sheetHeaders.includes(h)
                );

                if (missing.length > 0) {
                    setMissingHeaders(missing);
                    setMissingHeadersModal(true);
                    setUploading(false);
                    return;
                }

                const { errors, rowErrorMap } = validateExcelData(
                    sheetHeaders,
                    sheetData,
                    requiredHeaders
                );

                setHeaders(sheetHeaders);
                setRows(sheetData);
                setValidationData(errors);
                setRowErrors(rowErrorMap);

                setIsModalVisible(true);

                if (errors.length)
                    message.warning(`${errors.length} validation issue(s).`);
                else message.success("Excel validated successfully!");

                setUploading(false);
            },
            () => {
                message.error("Failed to read Excel file.");
                setUploading(false);
            }
        );
    };


    const handleCellChange = (rowKey, column, value) => {
        const updatedRows = rows.map((row) =>
            row.key === rowKey ? { ...row, [column]: value } : row
        );
        setRows(updatedRows);
        const { rowErrorMap } = validateExcelData(headers, updatedRows, requiredHeaders);
        setRowErrors(rowErrorMap);
    };

    const handleSave = () => {
        const { errors, rowErrorMap } = validateExcelData(
            headers,
            rows,
            requiredHeaders
        );

        setValidationData(errors);
        setRowErrors(rowErrorMap);

        if (errors.length > 0 && fileType !== "daily") {
            message.error(`Fix ${errors.length} validation error(s).`);
            return;
        }
        const payload = generateStaffMonthlyPayload(rows, fileType, dateString, config, filter);
        if (fileType === "monthly") {
            dispatch(importStaff(get(filter, "company.id"), payload))
                .then(res => {
                    message.success(STAFF_IMPORT_SUCCESS)
                    setIsModalVisible(false);
                })
                .catch(error => {
                    message.error(STAFF_IMPORT_FAILED)
                })
        }
        if (fileType === "daily") {
            dispatch(importAttendence(get(filter, "company.id"), payload))
                .then(res => {
                    message.success(STAFF_ATTENDENCE_IMPORT_SUCCESS)
                    setIsModalVisible(false);
                })
                .catch(error => {
                    message.error(STAFF_ATTENDENCE_IMPORT_FAILED)
                })
        }
    };

    const cleanedRows = removeSNFields
        ? rows.map((row) => {
            const { "S.N.": _, SN: __, SNO: ___, ...rest } = row;
            return rest;
        })
        : rows;
    const rowsWithShift = cleanedRows.filter(
        (row) => fileType !== "daily" || Boolean(row["Shift"])
    );
    const filteredHeaders = removeSNFields
        ? headers.filter(
            (h) => !["s.n.", "sn", "sno"].includes(h.toLowerCase())
        )
        : headers;

    const columns = [{
        title: "S.N.",
        dataIndex: "index",
        width: 70,
        fixed: "left",
        render: (_, __, index) => index + 1
    }, ...generateStaffColumns(
        filteredHeaders,
        rowErrors,
        handleCellChange,
        config, filter
    )];
    return (
        <div>
            <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                beforeUpload={(file) => {
                    handleUpload(file);
                    return false;
                }}
            >
                <Button icon={<UploadOutlined />} loading={uploading}>
                    {title || "Select Excel"}
                </Button>
            </Upload>
            <Modal
                title={<span style={{ color: "red" }}>Validation Result</span>}
                open={isModalVisible}
                width="90vw"
                onCancel={() => setIsModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsModalVisible(false)}>
                        Close
                    </Button>,
                    <>
                        {rowsWithShift.length > 0 ? (
                            <Button
                                key="save"
                                type="primary"
                                onClick={handleSave}
                                disabled={Object.keys(rowErrors).length > 0 && fileType !== 'daily'}
                            >
                                Save & Re-Validate
                            </Button>
                        ) : null}
                    </>
                    ,
                ]}
                afterClose={updateData}
            >
                {validationData.length === 0 || fileType === 'daily' ? (
                    <div style={{ color: "#1677ff" }}>No validation errors</div>
                ) : (
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 10,
                        }}
                    >
                        {map(validationData, (err) => (
                            <div
                                key={crypto.randomUUID()}
                                style={{
                                    background: "#fff1f0",
                                    padding: 10,
                                    borderLeft: "5px solid red",
                                    flex: "1 1 300px",
                                    minWidth: 300,
                                }}
                            >
                                <b>{err.context}:</b> {err.message}
                            </div>
                        ))}
                    </div>

                )}
                {rowsWithShift.length > 0 ? (

                    <Table
                        className="mt-4"
                        columns={columns}
                        dataSource={rowsWithShift}
                        rowKey={(record) => get(record, "id") || crypto.randomUUID()}
                        pagination={false}
                        size="small"
                    />
                ) : (
                    <div className="px-8 py-8">
                        <Result
                            title="Excel does not have required fields"
                            subTitle="Please upload proper excel file"
                            status="error"
                        />
                    </div>
                )}

            </Modal>
            <HeadersMissingModal missingHeadersModal={missingHeadersModal} setMissingHeadersModal={setMissingHeadersModal} missingHeaders={missingHeaders} />
        </div>
    );
};

export default StaffAttendance;
